import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const dagIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DAG = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const MND = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

const LocKnop = ({ l, gekozen, onKies }) => (
  <button type="button" data-testid={`nieuw-bezoek-loc-${l}`} aria-pressed={gekozen} onClick={() => onKies(l)}
    className={`min-h-[44px] px-3 rounded-md border text-sm font-semibold text-left transition-colors ${gekozen ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-300 text-zinc-800 hover:bg-zinc-50"}`}>{l}</button>
);

// Bezoek vastleggen: locatie (eigen locaties eerst) → datum (vandaag, tot 7 dagen terug) → aanmaken en openen.
export const NieuwBezoek = ({ open, onOpenChange, meta, startLocatie }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loc, setLoc] = useState(startLocatie || "");
  const [datum, setDatum] = useState(dagIso(new Date()));
  const [bezig, setBezig] = useState(false);
  useEffect(() => { if (open) { setLoc(startLocatie || ""); setDatum(dagIso(new Date())); } }, [open, startLocatie]);

  const { eigen, rest } = useMemo(() => {
    const locs = (meta?.locations || []).filter((l) => !(meta?.generalBuckets || []).includes(l));
    const lo = meta?.locationOms || {};
    const mijn = user.role === "oversight" ? [] : locs.filter((l) => (lo[l] || []).includes(user.username));   // R1 C1: oversight ziet alles
    return { eigen: mijn, rest: locs.filter((l) => !mijn.includes(l)) };
  }, [meta, user.username, user.role]);
  const dagen = useMemo(() => Array.from({ length: 8 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - i); return d; }), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const maak = async () => {
    setBezig(true);
    try {
      const r = await api.post("/bezoeken", { locatie: loc, datum });
      onOpenChange(false);
      navigate(`/bezoeken/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vastleggen mislukt"); } finally { setBezig(false); }
  };
  const knoppen = (lijst) => lijst.map((l) => <LocKnop key={l} l={l} gekozen={loc === l} onKies={setLoc} />);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nieuw-bezoek-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-heading font-black">Bezoek vastleggen</DialogTitle>
        <DialogDescription>Kies de locatie en de dag van je bezoek.</DialogDescription>
        <div className="space-y-4">
          {eigen.length > 0 && <div><div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">Jouw locaties</div><div className="grid grid-cols-2 gap-2">{knoppen(eigen)}</div></div>}
          <div><div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">{eigen.length ? "Andere locaties" : "Locaties"}</div><div className="grid grid-cols-2 gap-2">{knoppen(rest)}</div></div>
          <div>
            <label htmlFor="nieuw-bezoek-datum" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Dag</label>
            <select id="nieuw-bezoek-datum" data-testid="nieuw-bezoek-datum" value={datum} onChange={(e) => setDatum(e.target.value)}
              className="mt-1.5 w-full min-h-[44px] px-3 rounded-md border border-zinc-300 bg-white text-base">
              {dagen.map((d, i) => <option key={i} value={dagIso(d)}>{i === 0 ? "Vandaag" : i === 1 ? "Gisteren" : ""}{i < 2 ? " · " : ""}{DAG[d.getDay()]} {d.getDate()} {MND[d.getMonth()]}</option>)}
            </select>
          </div>
          <button type="button" data-testid="nieuw-bezoek-maak" disabled={!loc || bezig} onClick={maak}
            className="w-full min-h-[48px] rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors">
            {bezig ? "Bezig…" : loc ? `Bezoek aan ${loc} vastleggen` : "Kies eerst een locatie"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
